import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Container, Row, Col, Card, Table, Spinner, Alert, Badge, ProgressBar } from 'react-bootstrap'
import client from '../../api/client'

function PollResults() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [poll, setPoll] = useState(null)
  const [results, setResults] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchResults()
  }, [id])

  const fetchResults = async () => {
    setLoading(true)
    try {
      const [pollResponse, resultsResponse] = await Promise.all([
        client.get(`/admin/api/v1/polls/${id}`),
        client.get(`/admin/api/v1/polls/${id}/results`)
      ])
      setPoll(pollResponse.data)
      setResults(resultsResponse.data)
    } catch (err) {
      console.error('Error fetching results:', err)
      setError('Erreur lors du chargement des résultats')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const renderRatingResults = (questionResults) => {
    const distribution = questionResults.distribution || {}
    const average = questionResults.average || 0
    const total = questionResults.total || 0

    return (
      <div>
        <div className="mb-3">
          <h5>Moyenne: {average.toFixed(2)} / 5.0</h5>
          <div className="d-flex align-items-center">
            {[1, 2, 3, 4, 5].map((star) => (
              <span key={star} style={{ fontSize: '1.5rem', color: star <= Math.round(average) ? '#ffc107' : '#dee2e6' }}>
                ★
              </span>
            ))}
            <span className="ms-2 text-muted">({total} réponses)</span>
          </div>
        </div>

        <div>
          <h6>Distribution:</h6>
          {[5, 4, 3, 2, 1].map((rating) => {
            const count = distribution[rating] || 0
            const percentage = total > 0 ? (count / total) * 100 : 0
            return (
              <div key={rating} className="mb-2">
                <div className="d-flex justify-content-between mb-1">
                  <small>{rating} étoile{rating > 1 ? 's' : ''}</small>
                  <small>{count} ({percentage.toFixed(1)}%)</small>
                </div>
                <ProgressBar
                  now={percentage}
                  variant={rating >= 4 ? 'success' : rating >= 3 ? 'warning' : 'danger'}
                />
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const renderChoiceResults = (questionResults) => {
    const options = questionResults.options || []
    const total = questionResults.total || 0

    return (
      <div>
        <h6>{total} réponse{total > 1 ? 's' : ''}</h6>
        {options.map((option, index) => {
          const percentage = total > 0 ? (option.count / total) * 100 : 0
          return (
            <div key={index} className="mb-3">
              <div className="d-flex justify-content-between mb-1">
                <strong>{option.option}</strong>
                <span>{option.count} ({percentage.toFixed(1)}%)</span>
              </div>
              <ProgressBar
                now={percentage}
                variant="primary"
                label={`${percentage.toFixed(0)}%`}
              />
            </div>
          )
        })}
      </div>
    )
  }

  const renderTextResults = (questionResults) => {
    const responses = questionResults.responses || []

    return (
      <div>
        <h6>{responses.length} réponse{responses.length > 1 ? 's' : ''}</h6>
        {responses.length === 0 ? (
          <p className="text-muted">Aucune réponse</p>
        ) : (
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>Utilisateur</th>
                  <th>Réponse</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {responses.map((response, index) => (
                  <tr key={index}>
                    <td>
                      <small>{response.user_email || 'Anonyme'}</small>
                    </td>
                    <td>{response.answer}</td>
                    <td>
                      <small className="text-muted">{formatDate(response.created_at)}</small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>
    )
  }

  const getQuestionTypeLabel = (type) => {
    const labels = {
      rating: 'Note (1-5)',
      text: 'Texte libre',
      choice: 'Choix multiple'
    }
    return labels[type] || type
  }

  if (loading) {
    return (
      <Container fluid>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Chargement...</span>
          </Spinner>
        </div>
      </Container>
    )
  }

  if (error || !poll || !results) {
    return (
      <Container fluid>
        <Alert variant="danger">{error || 'Sondage introuvable'}</Alert>
      </Container>
    )
  }

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <h2>📊 Résultats du sondage</h2>
        </Col>
      </Row>

      <Card className="mb-4">
        <Card.Header>
          <strong>{poll.title}</strong>
        </Card.Header>
        <Card.Body>
          {poll.description && <p className="text-muted">{poll.description}</p>}
          <Row>
            <Col md={3}>
              <small className="text-muted">Contexte:</small><br />
              <Badge bg="info">{poll.context_type}</Badge>
              {poll.context_value && <> <small>{poll.context_value}</small></>}
            </Col>
            <Col md={3}>
              <small className="text-muted">Réponses:</small><br />
              <strong>{results.total_responses || 0}</strong>
            </Col>
            <Col md={3}>
              <small className="text-muted">Début:</small><br />
              <small>{formatDate(poll.starts_at)}</small>
            </Col>
            <Col md={3}>
              <small className="text-muted">Fin:</small><br />
              <small>{formatDate(poll.ends_at)}</small>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {results.questions && results.questions.length > 0 ? (
        results.questions.map((questionResult, index) => {
          const question = poll.questions.find(q => q.id === questionResult.question_id)
          if (!question) return null

          return (
            <Card key={questionResult.question_id} className="mb-3">
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <Badge bg="secondary" className="me-2">Question {index + 1}</Badge>
                    <Badge bg="primary">{getQuestionTypeLabel(question.question_type)}</Badge>
                  </div>
                </div>
              </Card.Header>
              <Card.Body>
                <h5 className="mb-3">{question.question_text}</h5>

                {question.question_type === 'rating' && renderRatingResults(questionResult)}
                {question.question_type === 'choice' && renderChoiceResults(questionResult)}
                {question.question_type === 'text' && renderTextResults(questionResult)}
              </Card.Body>
            </Card>
          )
        })
      ) : (
        <Alert variant="info">Aucune réponse pour ce sondage</Alert>
      )}

      <div className="mb-4">
        <button className="btn btn-outline-secondary" onClick={() => navigate('/polls')}>
          ← Retour à la liste
        </button>
      </div>
    </Container>
  )
}

export default PollResults
